import { useEffect, useState } from 'react';

import CategoryMultiSelect from '../../components/CategoryMultiSelect';
import ContactMethodsEditor from '../../components/ContactMethodsEditor';
import FormField from '../../components/ui/FormField';
import StatusMessage from '../../components/ui/StatusMessage';
import { normalizeIdArray } from '../../utils/collectionUtils';
import {
  buildSupplierFormPayload,
  createEmptySupplierForm,
  getFieldValue,
  getSupplierFormInitialData,
  setFieldValue,
  supplierFormSections,
  validateSupplierForm,
} from './supplierFormUtils';

export default function SupplierForm({
  initialData,
  onSubmit,
  submitLabel = 'Save Supplier',
  isSubmitting = false,
  errorMessage = '',
  title,
}) {
  const [form, setForm] = useState(createEmptySupplierForm);
  const [validationMessage, setValidationMessage] = useState('');

  useEffect(() => {
    setForm(getSupplierFormInitialData(initialData));
    setValidationMessage('');
  }, [initialData]);

  const handleSubmit = (event) => {
    event.preventDefault();

    const nextValidationMessage = validateSupplierForm(form);

    if (nextValidationMessage) {
      setValidationMessage(nextValidationMessage);
      return;
    }

    setValidationMessage('');
    onSubmit(buildSupplierFormPayload(form));
  };

  const pageStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    maxWidth: 760,
  };

  const formStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 20,
    borderRadius: 16,
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
  };

  const titleStyle = {
    margin: 0,
    fontSize: 28,
    color: '#0f172a',
  };

  const buttonStyle = {
    alignSelf: 'flex-start',
    border: 'none',
    borderRadius: 10,
    padding: '10px 16px',
    backgroundColor: '#0f172a',
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 600,
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    opacity: isSubmitting ? 0.6 : 1,
  };

  return (
    <div style={pageStyle}>
      <h2 style={titleStyle}>{title || (initialData ? 'Edit Supplier' : 'Add Supplier')}</h2>
      <StatusMessage tone="error" message={validationMessage || errorMessage} />

      <form onSubmit={handleSubmit} style={formStyle}>
        {supplierFormSections.map((section) => (
          <fieldset key={section.title} className="form-section">
            <legend>{section.title}</legend>
            <div className="form-grid">
              {section.fields.map((field) => (
                <FormField
                  key={field.name}
                  name={field.name}
                  label={field.label}
                  type={field.type}
                  required={field.required}
                  value={getFieldValue(form, field.name)}
                  onChange={(value) => {
                    setForm((prev) => setFieldValue(prev, field.name, value));
                    if (validationMessage) {
                      setValidationMessage('');
                    }
                  }}
                />
              ))}
            </div>
          </fieldset>
        ))}

        <ContactMethodsEditor
          value={form.contactMethods}
          onChange={(contactMethods) => {
            setForm((prev) => ({ ...prev, contactMethods }));
            if (validationMessage) {
              setValidationMessage('');
            }
          }}
          disabled={isSubmitting}
          idPrefix="supplier-form-contact-method"
        />

        <div>
          <label
            style={{
              display: 'block',
              marginBottom: 8,
              color: '#0f172a',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Categories
          </label>
          <CategoryMultiSelect
            value={form.categories}
            onChange={(newCategories) =>
              setForm((prev) => ({
                ...prev,
                categories: normalizeIdArray(newCategories),
              }))
            }
          />
        </div>

        <button type="submit" disabled={isSubmitting} style={buttonStyle}>
          {submitLabel}
        </button>
      </form>
    </div>
  );
}
